import React, { useState, useEffect } from 'react';
import { Link as RouterLink, Route, Routes, useLocation } from 'react-router-dom';
import Axios from 'axios';
import { Helmet } from 'react-helmet-async';

import { SingleTrend } from './SingleTrend';
import { Loading } from './Loading';

const key = 'dqweqwe123y97qhduiasd$@^!*#&bjasdja&^&@!#';

function slugify(s) {
  return String(s || '').toLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '');
}

const IconArrowRight = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
    <path d="M5 12h14M13 5l7 7-7 7" />
  </svg>
);

/* ═══════════════════ TrendsList - article + sidebar ═══════════════════ */

export const TrendsList = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [trends, setTrends] = useState([]);
  const [metadata, setMetadata] = useState(null);

  useEffect(() => {
    Axios.post('/new_apis/getsinglemetadata', { type: 'trends' }, { headers: { key } }).then((res) => {
      if (res.data.status) setMetadata(res.data.metadata[0]);
    });
    Axios.get('/new_apis/get-trends-list', { headers: { key } }).then((res) => {
      setTrends(res.data.trends || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;

  return (
    <>
      {metadata && (
        <Helmet>
          <title>{metadata.seo_title}</title>
          <meta name="description" content={metadata.seo_desc} />
          <meta name="keywords" content={metadata.seo_keywords} />
        </Helmet>
      )}

      <PageContainerCustom>
        {/* Layout: article left (wide), sidebar right (narrow) */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_minmax(260px,28%)] gap-5 md:gap-6 py-5 pb-10">
          {/* Article column - SingleTrend self-gates for guests */}
          <div className="min-w-0">
            <Routes>
              <Route path="trend/:slug?/:id?" element={<SingleTrend />} />
            </Routes>
          </div>

          {/* Sidebar - amber bg, magazine-style article previews */}
          <aside
            className="
              relative isolate overflow-hidden rounded-2xl
              bg-amber-gradient
              p-3
              md:sticky md:top-[68px] md:self-start
              md:max-h-[calc(100vh-92px)]
              shadow-[0_10px_28px_-12px_rgba(10,10,11,0.45)]
              hidden md:block
            "
          >
            {/* noise overlay */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 -z-10 opacity-[0.08] mix-blend-multiply"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
              }}
            />
            {/* dark gradient hairline at top */}
            <span aria-hidden className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-ink/35 to-transparent" />

            <div className="md:max-h-[calc(100vh-130px)] md:overflow-y-auto md:pr-1 -mr-1">
              {/* Header */}
              <div className="mb-3 px-1.5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span aria-hidden className="inline-flex h-1.5 w-1.5 rounded-full" style={{ backgroundColor: '#0A0A0B', boxShadow: '0 0 8px rgba(10,10,11,0.4)' }} />
                  <span className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: '#0A0A0B' }}>
                    Other trends
                  </span>
                </div>
              </div>

              <nav className="flex flex-col gap-1.5">
                {trends.length === 0 ? (
                  <div className="px-2.5 py-2 h-3 rounded animate-pulse" style={{ backgroundColor: 'rgba(10,10,11,0.18)' }} />
                ) : (
                  trends.map((item) => {
                    const to = `/trends-list/trend/${slugify(item.blog_title)}/${item.blog_id}`;
                    const isActive = location.pathname === to;
                    return (
                      <RouterLink
                        key={item.blog_id}
                        to={to}
                        className="
                          group relative flex items-start gap-3 rounded-lg p-2
                          transition-[background-color,transform] duration-200 ease-out-soft
                        "
                        style={{
                          backgroundColor: isActive ? '#0A0A0B' : 'transparent',
                        }}
                        onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = 'rgba(10,10,11,0.10)'; }}
                        onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
                      >
                        {/* Active indicator bar */}
                        <span
                          aria-hidden
                          className="pointer-events-none absolute -left-1 top-1/2 -translate-y-1/2 rounded-r-full transition-[width,opacity] duration-300 ease-out-soft"
                          style={{
                            width: isActive ? 3 : 0,
                            opacity: isActive ? 1 : 0,
                            height: '60%',
                            backgroundColor: isActive ? '#FBBF24' : '#0A0A0B',
                          }}
                        />

                        {/* Thumbnail */}
                        <div
                          className="relative shrink-0 overflow-hidden rounded-md"
                          style={{
                            width: 56,
                            height: 56,
                            backgroundColor: 'rgba(10,10,11,0.14)',
                            outline: isActive ? '1.5px solid rgba(251,191,36,0.6)' : '1px solid rgba(10,10,11,0.18)',
                          }}
                        >
                          {item.blog_image ? (
                            <img
                              src={item.blog_image}
                              alt=""
                              loading="lazy"
                              className="h-full w-full object-cover transition-transform duration-500 ease-out-soft group-hover:scale-110"
                            />
                          ) : (
                            <div className="h-full w-full flex items-center justify-center text-[10px] font-bold tracking-wider" style={{ color: 'rgba(10,10,11,0.4)' }}>
                              ESAY
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex min-w-0 flex-1 flex-col gap-1">
                          <h4
                            className="line-clamp-2 text-[13px] leading-[1.35] tracking-[-0.005em] transition-colors duration-200"
                            style={{
                              color: isActive ? '#FBBF24' : 'rgba(10,10,11,0.85)',
                              fontWeight: isActive ? 700 : 600,
                            }}
                          >
                            {item.blog_title}
                          </h4>
                          {item.added_date && (
                            <div
                              className="flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-[0.16em]"
                              style={{ color: isActive ? 'rgba(251,191,36,0.7)' : 'rgba(10,10,11,0.55)' }}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-2.5 w-2.5 shrink-0" aria-hidden>
                                <rect x="3" y="4" width="18" height="18" rx="2" />
                                <path d="M16 2v4M8 2v4M3 10h18" />
                              </svg>
                              <span className="truncate">{item.added_date}</span>
                            </div>
                          )}
                        </div>
                      </RouterLink>
                    );
                  })
                )}
              </nav>

              {/* View-all footer */}
              <div className="mt-3 pt-3 border-t" style={{ borderColor: 'rgba(10,10,11,0.18)' }}>
                <RouterLink
                  to="/trends"
                  className="
                    group inline-flex w-full items-center justify-between gap-1.5 rounded-lg px-2.5 py-2
                    text-[11px] font-bold uppercase tracking-[0.16em]
                    transition-[background-color] duration-200 ease-out-soft
                  "
                  style={{ color: '#0A0A0B' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(10,10,11,0.10)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <span>View all trends</span>
                  <IconArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" style={{ color: '#0A0A0B' }} />
                </RouterLink>
              </div>
            </div>
          </aside>
        </div>
      </PageContainerCustom>
    </>
  );
};

/* ─────────── Wider container (reading-friendly) ─────────── */

function PageContainerCustom({ children }) {
  return (
    <div className="mx-auto w-full max-w-[1320px] px-3 sm:px-5 lg:px-6">
      {children}
    </div>
  );
}
